import { ApolloError, DocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';

import { MessageType } from 'modules/graphql/types';
import { isDefined } from 'utils/tsHelpers';
import noop from 'utils/noop';
import { useGraphql } from 'utils/hooks/useGraphql';
import { SelectedMessageMedium } from '../common/types';

export type FetchMessageTypesByMediumQueryRequest = {
  messageMedium: SelectedMessageMedium;
};

type FetchMessageTypesByMediumQueryResponse = {
  messageTypes: MessageType[];
};

export type UseMessageTypesByMediumResponse = {
  errorMessage?: ApolloError;
  hasError: boolean;
  isLoading: boolean;
  refetch: (variables?: Partial<FetchMessageTypesByMediumQueryRequest>) => void;
  results: MessageType[];
};

export const fetchMessageTypesByMediumQuery = gql`
  query FetchMessageTypesByMedium($messageMedium: MessageMediumDefinition) {
    messageTypes(messageMedium: $messageMedium) {
      id
      name
      subscriptionPolicy
      messageChannel {
        id
        name
        channelType
      }
    }
  }
`;

export function useMessageTypesByMedium(
  options: Record<string, any>,
  query: DocumentNode = fetchMessageTypesByMediumQuery, // eslint-disable-line
): UseMessageTypesByMediumResponse {
  const queryOptions = useMemo(
    () => ({
      notifyOnNetworkStatusChange: true,
      ...options,
    }),
    [options],
  );
  const {
    data: fetchMessageTypesByMediumQueryData,
    error: hasError = false,
    errorMessage,
    isLoading,
    refetch,
  } = useGraphql(query, queryOptions);

  // protect against the undefined response while the query is running
  if (isDefined<FetchMessageTypesByMediumQueryResponse>(fetchMessageTypesByMediumQueryData)) {
    const results = fetchMessageTypesByMediumQueryData.messageTypes || {};

    return {
      errorMessage,
      hasError,
      isLoading,
      refetch,
      results,
    };
  }

  return {
    errorMessage,
    hasError,
    isLoading,
    refetch: noop,
    results: [],
  };
}
